import SockJS from "sockjs-client";
import { Client } from "@stomp/stompjs";

export const MESSAGE_MAX = 99;
export const MESSAGE_CUTOFF = 100;

export function getUnreadCount(acc, currentVal){
	if(!currentVal.isRead){
		return acc + 1;
	}
	return acc;
}

const MILI = 1000;
function generateTimestamp(){
	// Socket messages expect a timestamp in seconds since epoch, instead of ms.
	return Date.now() / MILI;
}

function createClientMessage(message){
	let messageId = message?.id || Date.now();
	let jobId = message.jobId || messageId;

	return {
		id: messageId,
		isRead: false,

		info: {
			...message,
			id: messageId,
			jobId,
			timestamp: generateTimestamp(),
		},
	};
}

export class FTSocket {
	// Default this in to the development API, though it should *always* be passed in when instantiating a new instance
	apiBase = "http://gateway.local:8090";

	// Current list of topics we're listening to
	topics = [];

	// All messages received while this instance has been instantiated
	messages = [];

	// Should we try to re-connect to the socket if the connection is lost?
	reconnect = true;

	client = null;
	__debug = false;

	constructor(apiBase, topics = []){
		this.apiBase = apiBase;
		this.topics = topics;

		this._buildClient();
	}

	_buildClient(){
		let { apiBase } = this;

		if(!apiBase){ throw "Missing API Base!"; }

		let debugInfo = {};

		if(this.__debug){
			debugInfo.debug = (str) => {
				/*eslint-disable*/
				console.group("WebSocket Debugging");
					console.info("Debugging from %cStopmJS%c","font-weight:bold; color:#c76eff;", "font-weight:normal; color:#000");
					console.log(str);
				console.groupEnd();
				/*eslint-enable*/
			};
		}	//end if(this.__debug)

		// Try disconnecting from the client first
		try {
			this.client.deactivate();
		} catch(e){}

		this.client = new Client({
			webSocketFactory() {
				return new SockJS(`${apiBase}/nfy/ws`);
			},
			...debugInfo,
			reconnectDelay: 60000,
		});
	}

	set debug(on){
		this.__debug = on;
		this._buildClient();
	}

	set reconnect(on){
		this.reconnect = on;
	}

	get unread(){
		return this.messages.reduce(getUnreadCount, 0);
	}

	addTopics(...what){
		/*eslint-disable*/
		if(this.__debug){
			console.group("Debug!");
				console.info("Adding a new topic:");
				console.log(what);
			console.groupEnd();
		}
		/*eslint-enable*/
		this.topics = [].concat(this.topics, what);

		if(this.reconnect){
			this.connect();
		}
	}

	removeTopics(...what){
		//TODO...
	}

	addMessage(topic, message){
		/*
			This manually adds a message to the list, without it coming over a socket.

			Your message will likely have to include a `component` property, if it's not
			one of the message types supported by default.

			For consistency with standard socket messages, your message should have the format:
			{
				id, // optional, will be set to Date.now() by default
				jobId, // optional, will be set to the same value as id by default
				origin,
				status,
				task,
			}
		*/
		let { messages, onMessage } = this;
		let newMessage = createClientMessage(message);

		this.messages = [newMessage, ...messages];
		onMessage(topic, newMessage.info);

		return newMessage.id;
	}

	updateMessage(topic, message){
		/*
			This replaces a message object that's in the message list with the new message,
			which is useful for things like status updates

			If the new message.id doesn't match a message in the list, it will throw an error.
		*/
		let { messages, onMessage } = this;
		let targetIndex = messages.findIndex(item => item.id === message.id);

		if(targetIndex !== -1){
			let newMessage = createClientMessage(message);
			this.messages[targetIndex] = newMessage;
			onMessage(topic, newMessage.info);
			return newMessage.info;
		}

		throw new Error("Attempted to update FTSocket message that doesn't exist.");
	}

	transformMessage(topic, messageId, transform = message => message){
		/*
			This allows you to update a message by specifying its id, and a transform function.
			This can be useful if you just want to change a property on a message, but leave the rest
			of it alone.

			If the messageId doesn't match a message in the list, it will throw an error.
		*/
		let { messages, onMessage } = this;
		let targetIndex = messages.findIndex(item => item.id === messageId);

		if(targetIndex !== -1){
			let message = messages[targetIndex];
			let newMessage = createClientMessage(transform(message.info));
			this.messages[targetIndex] = newMessage;

			onMessage(topic, newMessage.info);
			return newMessage.info;
		}

		throw new Error("Attempted to update FTSocket message that doesn't exist.");
	}

	/**
	 * [onMessage description]
	 * 		When a message is received we should also emmit a window event just in case the application needs to do other things too
	 * @param  {string} topicName The Topic which sent out this message/event
	 * @param  {object} mess      Full object received from the API
	 * @return {null}
	 */
	onMessage(topicName, mess){
		window.dispatchEvent(new CustomEvent("onFTMessage", { detail:{ topicName, message:mess } }));
	}

	connect(){
		let me = this
			, newMessages = []
			;

		let { client, topics, __debug, onMessage } = this;

		/*eslint-disable*/
		if(__debug){
			console.group("Debug!");
				console.info("Setting up connections:");
				console.log(topics);
			console.groupEnd();
		}
		/*eslint-enable*/


		client.onConnect = function(frame) {
			// Do something, all subscribes must be done is this callback
			// This is needed because this will be executed after a (re)connect
			topics.forEach((tt) => {
				let topicName = tt
					, baseMessage = {
						isRead:false,
						info:null,
					}
					;

				if(typeof(tt) === "object"){
					topicName = tt.name;
					baseMessage = {
						isRead:false,
						...tt,
					};
				}

				client.subscribe(topicName, (message) => {
					let info = {};

					newMessages = me.messages;

					try {
						info = JSON.parse(message.body);
					} catch(err){
						/*eslint-disable*/
						console.group("WebSocket Error");
							console.error("Problem parsing Message");
							console.error(err);
						console.groupEnd();
						/*eslint-enable*/
					}

					/**
					 * We can pass in custom formatters from the host application as an array on the topic
					 * The objects should have the following 2 (two) keys:
					 *
					 * 	shouldRender() 		- This is a function which takes the whole socket message and returns a boolean
					 *
					 *	component			- This should return a React Component for rendering
					 *
					 * If shouldRender() returns true, and if there isn't already a `component` on the message,
					 * 		the found `component` will be attached to the message
					 * 		Inside of `<MessageItem />` we use `component` to build a custom message
					 *
					 * This will allow us to `dispatch()` things from withn our applications
					 */
					if(tt.formatters){
						let match = tt.formatters.find((v) => {
							return v.shouldRender(info);
						});

						if(match?.component && !info.component){
							info.component = match.component;
						}
					}

					if(__debug){
						/*eslint-disable*/
						console.group("%cWebSocket%c Message!", "font-weight:bold; color:#c76eff; text-decoration:underline;", "font-weight:normal; color:#3d3d3d; text-decoration:none;");
							console.log("Message");
							console.info(info);
						console.groupEnd();
						/*eslint-enable*/
					}

					// If we want to connect to something directly
					if(tt.onMessage){
						tt.onMessage(info, message);
					}

					// Update existing messages based on `jobId`
					let existingMessage = newMessages.findIndex(v => v.info.jobId === info.jobId);

					if(existingMessage >= 0){
						newMessages = newMessages.filter((v) => {
							return v.info.jobId !== info.jobId;
						});
						// messages = messages.splice(existingMessage, 1);
					}

					// Always push new messages to the top of the list
					newMessages.unshift({
						...baseMessage,
						id:info.id,
						info,
					});

					me.messages = newMessages;

					// Send a message to the general listeners
					onMessage(topicName, info);
				});
			});
		};	//end client.onConnect()


		// TODO: figure out where to put this
		client.activate();
	}	//end connect()

	readAllMessages = () => {
		this.messages.forEach((v) => {
			let { status } = v?.info || {};
			if(!status || !/in progress/i.test(status)){
				v.isRead = true;
			}
		});
		this.onMessage("@@READ_ALL", this.messages);
	}

	readMessage = (...id) => {
		if(!id){ return; }

		let ids = [].concat(id)
			, { messages } = this
			, messLength = messages.length
			, mess = 0
			;

		for(mess; mess < messLength; mess++){
			let msg = messages[mess];

			if(ids.indexOf(msg.id) !== -1){
				msg.isRead = true;
			}
		}

		this.onMessage("@@READ_ITEM", this.messages);
	}

}	//end FTSocket{}

export default FTSocket;





